import { Fragment, useEffect, useState, useMemo, useCallback } from "react";
import { tr } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { getWorkOrdersGlobal, getClients, getTechnicians, getProjectManagers } from "@/lib/api";
import { QuickWOModal } from "@/components/workOrderTab/QuickWOModal";
import { Plus, Copy } from "lucide-react";
import { confirmFieldCompletion } from "@/lib/fieldCompletion";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { matchesCategories, recordCategories, useStickyCategories } from "@/components/common/CategoryFilter";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import StickyTop from "@/components/common/StickyTop";

import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
import { BTN } from "@/components/common/btn";
import { shortDate, money } from "@/lib/format";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600",
  submitted: "bg-orange-100 text-orange-700",
  approved: "bg-green-100 text-green-700",
  on_hand: "bg-teal-100 text-teal-700",
  on_hold: "bg-rose-100 text-rose-700",
  in_progress: "bg-amber-100 text-amber-700",
  scheduled: "bg-blue-100 text-blue-700",
  en_route: "bg-purple-100 text-purple-700",
  on_site: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  on_hand: "On hand",
  on_hold: "On hold",
  in_progress: "In progress",
  scheduled: "Scheduled",
  en_route: "En route",
  on_site: "On site",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_ORDER = ["draft", "submitted", "approved", "on_hand", "on_hold", "scheduled", "in_progress", "en_route", "on_site", "field_complete", "completed", "cancelled"];

// Techs can only mark a WO "field complete" — it stays on the active list until the PM confirms it.
const isFieldComplete = (wo) => !!wo.field_complete && wo.status !== "completed" && wo.status !== "cancelled";
const statusKey = (wo) => (isFieldComplete(wo) ? "field_complete" : wo.status);

// Field-complete rows show an outlined green "Confirm" pill (same size as the others); confirming
// acknowledges the close-out and pushes the WO to Completed, exactly like the Ops dashboard.
const WOStatusBadge = ({ wo, onConfirm }) => isFieldComplete(wo) ? (
  <button type="button"
    className="inline-flex w-[88px] items-center justify-center rounded-md border border-emerald-500 bg-transparent px-2.5 py-0.5 text-[10px] font-semibold text-emerald-600 whitespace-nowrap hover:bg-emerald-50 transition-colors"
    title={`Marked done in the field by ${wo.field_completed_by || "a technician"} — confirm to complete and archive`}
    onClick={(e) => { e.stopPropagation(); onConfirm(wo); }}
    data-testid={`wo-status-field-complete-${wo.id}`}>
    {tr("Confirm")}
  </button>
) : (
  <Badge variant="secondary" className={`inline-flex w-[88px] justify-center text-[10px] whitespace-nowrap ${STATUS_COLORS[wo.status] || ""}`}>{tr(STATUS_LABELS[wo.status] || wo.status)}</Badge>
);

const SORT_KEY = "abs.wo.sort";
const VIEWS = { active: "active", archived: "archived" };

const sortValue = (wo, field) => {
  switch (field) {
    case "wo_number": return (wo.wo_number || "").toLowerCase();
    case "title": return (wo.title || "").toLowerCase();
    case "client_name": return (wo.client_name || "").toLowerCase();
    case "site_address": return (wo.site_address || wo.site_location || "").toLowerCase() || "\uffff";
    case "status": {
      const i = STATUS_ORDER.indexOf(statusKey(wo));
      return i === -1 ? STATUS_ORDER.length : i;
    }
    case "category": return recordCategories(wo).join(", ").toLowerCase();
    case "total": return wo.total || 0;
    case "scheduled_date": return wo.scheduled_date || "";
    case "completion_date": return wo.completion_date || "";
    default: return "";
  }
};

const GROUP_KEY = "abs.wo.groupBy";
// Pill order = object order: Status · Customer · Category.
const GROUPS = {
  status: { label: "Status", of: (wo) => (statusKey(wo) === "field_complete" ? "Confirm" : (STATUS_LABELS[wo.status] || wo.status || "—")) },
  customer: { label: "Customer", of: (wo) => wo.client_name || "No customer" },
  category: { label: "Category", of: (wo) => recordCategories(wo).join(", ") || "No category" },
};

// Hoisted so header filter popovers never remount on a parent re-render.
const Th = ({ field, children, sort, onSort, filter, className = "" }) => (
  <TableHead className={`text-xs ${className}`}>
    <SortableHeader label={children} sorted={sort.field === field} direction={sort.dir} onSort={() => onSort(field)} filter={filter} testId={`wo-sort-${field}`} />
  </TableHead>
);

function WORow({ wo, view, onOpen, onConfirm, onQuickFrom }) {
  const done = wo.status === "completed" || wo.status === "cancelled";
  const cancelled = wo.status === "cancelled";
  return (
    <TableRow
      className={`cursor-pointer transition-colors ${cancelled ? "bg-red-50/40 hover:bg-red-50/70" : "hover:bg-slate-50"}`}
      onClick={() => onOpen(wo)}
      title={cancelled && wo.cancellation_reason ? `Cancelled — ${wo.cancellation_reason}` : undefined}
      data-testid={done ? `wo-completed-row-${wo.id}` : `wo-row-${wo.id}`}
    >
      <TableCell className={`font-mono text-xs font-semibold ${cancelled ? "text-red-600 line-through" : "text-brand"}`} data-testid={`wo-number-${wo.id}`}>
        {wo.wo_number}
      </TableCell>
      <TableCell className={`text-xs font-medium ${done ? "text-slate-600" : "text-slate-800"}`}>
        {wo.title || "Untitled"}
        {cancelled && wo.cancellation_reason && (
          <span className="block text-[10px] text-red-600/90 truncate max-w-[320px]" data-testid={`wo-cancel-reason-${wo.id}`}>
            Cancelled — {wo.cancellation_reason}
          </span>
        )}
      </TableCell>
      <TableCell className="text-xs text-slate-600">{wo.client_name}</TableCell>
      <TableCell className="text-xs text-slate-500 whitespace-nowrap" title={wo.site_name || undefined} data-testid={`wo-address-${wo.id}`}>{wo.site_address || wo.site_location || <span className="text-slate-300">—</span>}</TableCell>
      <TableCell><WOStatusBadge wo={wo} onConfirm={onConfirm} /></TableCell>
      <TableCell className="text-xs text-slate-600" data-testid={`wo-category-${wo.id}`}>{recordCategories(wo).join(", ") || <span className="text-slate-300">—</span>}</TableCell>
      <TableCell className="text-xs font-medium num">{money(wo.total)}</TableCell>
      <TableCell className="text-xs text-slate-500 num">{shortDate(wo.scheduled_date)}</TableCell>
      <TableCell className="text-xs text-slate-500 num">{shortDate(wo.completion_date)}</TableCell>
      <TableCell className="w-8 px-1" onClick={(e) => e.stopPropagation()}>
        {!done && onQuickFrom && (
          <button type="button" onClick={() => onQuickFrom(wo)} title={`Quick WO from ${wo.wo_number} — copies customer, site and lines`} data-testid={`wo-quick-from-${wo.id}`}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-emerald-50 hover:text-emerald-700">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function WorkOrdersPage() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cats, setCats] = useStickyCategories("abs.wo.categoryFilter");
  const [view, setView] = useState(VIEWS.active);
  const [statusFilter, setStatusFilter] = useState([]);
  // Quick WO (fresh from the toolbar, or copied from a row)
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickParent, setQuickParentRaw] = useState(null);
  const setQuickParent = (wo) => { setQuickParentRaw(wo); if (wo) setQuickOpen(true); };
  const [quickRefs, setQuickRefs] = useState({ clients: [], technicians: [], projectManagers: [] });
  useEffect(() => {
    if (!quickOpen || quickRefs.clients.length) return;
    Promise.all([getClients(), getTechnicians(), getProjectManagers()])
      .then(([c, t, p]) => setQuickRefs({ clients: c.data || [], technicians: t.data || [], projectManagers: p.data || [] }))
      .catch(() => {});
  }, [quickOpen, quickRefs.clients.length]);
  const [groupBy, setGroupBy] = useState(() => { try { const v = localStorage.getItem(GROUP_KEY) || ""; return GROUPS[v] ? v : ""; } catch { return ""; } });
  // Pressing the active grouping pill turns grouping off.
  const pickGroup = (k) => setGroupBy((cur) => { const n = cur === k ? "" : k; try { localStorage.setItem(GROUP_KEY, n); } catch { /* ignore */ } return n; });
  const [customerFilter, setCustomerFilter] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [sort, setSort] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(SORT_KEY) || "null");
      if (raw && typeof raw.field === "string") return { field: raw.field, dir: raw.dir === "desc" ? "desc" : "asc" };
    } catch { /* ignore */ }
    return { field: "", dir: "asc" };
  });

  const handleSort = useCallback((field) => {
    setSort((prev) => {
      const next = prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" };
      try { localStorage.setItem(SORT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const sortRows = useCallback((rows) => {
    if (!sort.field) return rows;
    return [...rows].sort((a, b) => {
      const aVal = sortValue(a, sort.field);
      const bVal = sortValue(b, sort.field);
      if (aVal < bVal) return sort.dir === "asc" ? -1 : 1;
      if (aVal > bVal) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [sort]);

  const loadWOs = async () => {
    try {
      const res = await getWorkOrdersGlobal();
      setWorkOrders(res.data);
    } catch {
      toast.error("Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWOs(); }, []);

  const inView = useCallback((wo) => {
    const closed = wo.status === "completed" || wo.status === "cancelled";
    return view === VIEWS.archived ? closed : !closed;
  }, [view]);

  // Rows of the current view before the header filters — the filter menus list what is actually there.
  // A search looks across every view (active + archived) so a WO is always findable.
  const viewRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const noteText = (wo) => (wo.note_log || []).map((n) => n.text || "").join(" ").toLowerCase();
    return workOrders.filter((wo) => {
      if (!q) return inView(wo);
      return (
        (wo.wo_number || "").toLowerCase().includes(q) ||
        (wo.title || "").toLowerCase().includes(q) ||
        (wo.client_name || "").toLowerCase().includes(q) ||
        (wo.site_name || "").toLowerCase().includes(q) ||
        (wo.site_location || "").toLowerCase().includes(q) ||
        (wo.site_address || "").toLowerCase().includes(q) ||
        noteText(wo).includes(q)
      );
    });
  }, [workOrders, search, inView]);

  const categoryOptions = useMemo(() => {
    const counts = {};
    viewRows.forEach((wo) => recordCategories(wo).forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
    return Object.keys(counts).sort((a, b) => a.localeCompare(b)).map((k) => ({ value: k, label: k, count: counts[k] }));
  }, [viewRows]);

  const customerOptions = useMemo(() => {
    const counts = {};
    viewRows.forEach((wo) => { const k = wo.client_name || ""; if (k) counts[k] = (counts[k] || 0) + 1; });
    return Object.keys(counts).sort((a, b) => a.localeCompare(b)).map((k) => ({ value: k, label: k, count: counts[k] }));
  }, [viewRows]);

  const statusOptions = useMemo(() => {
    const counts = {};
    viewRows.forEach((wo) => { const k = statusKey(wo); counts[k] = (counts[k] || 0) + 1; });
    return Object.keys(counts)
      .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
      .map((k) => ({ value: k, label: k === "field_complete" ? "Confirm" : (STATUS_LABELS[k] || k), count: counts[k] }));
  }, [viewRows]);

  const rows = useMemo(() => sortRows(viewRows.filter((wo) =>
    (!customerFilter || wo.client_name === customerFilter) &&
    matchesCategories(wo, cats) &&
    (!statusFilter.length || statusFilter.includes(statusKey(wo))))), [viewRows, customerFilter, cats, statusFilter, sortRows]);

  // Grouped by category: [{ label, rows }] — current sort within each group, groups alphabetical.
  const grouped = useMemo(() => {
    const g = GROUPS[groupBy];
    if (!g) return null;
    const by = new Map();
    rows.forEach((wo) => { const k = g.of(wo); if (!by.has(k)) by.set(k, []); by.get(k).push(wo); });
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, list]) => ({ label, rows: list }));
  }, [rows, groupBy]);

  // Full-page work order (UI sweep batch 5) — the slideout is gone.
  const handleOpenWO = useCallback((wo) => navigate(`/work-orders/${wo.id}`), [navigate]);

  const runConfirm = async () => {
    if (!confirming) return;
    setConfirmBusy(true);
    try {
      await confirmFieldCompletion(confirming.id);
      toast.success(`${confirming.wo_number} confirmed — completed and archived`);
      setConfirming(null);
      await loadWOs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't confirm the work order");
    } finally {
      setConfirmBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
      </div>
    );
  }

  // Pressing the already-chosen outside view returns to Active.
  const pickView = (v) => setView((cur) => (cur === v ? VIEWS.active : v));
  const hdr = { sort, onSort: handleSort };

  return (
    <div className="flex flex-col gap-2" data-testid="work-orders-page">
      <StickyTop testId="wo-sticky-top">
      {/* Toolbar — search · category · view pills · count far right */}
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="wo-toolbar">
        <SearchBar value={search} onChange={setSearch} testId="wo-global-search" placeholder={tr("Search...")} />
        <PillRail testId="wo-view-pills">
          {Object.entries(GROUPS).map(([k, g]) => (
            <Pill key={k} on={groupBy === k} onClick={() => pickGroup(k)} testId={`wo-group-${k}`} title={groupBy === k ? tr("Stop grouping") : tr("Group the list by {what}", { what: tr(g.label).toLowerCase() })}>{tr(g.label)}</Pill>
          ))}
          <PillDivider />
          <Pill on={view === VIEWS.archived} onClick={() => pickView(VIEWS.archived)} testId="toggle-archived-wos"
                title={view === VIEWS.archived ? "Back to active work orders" : "Show completed and cancelled work orders"}>{tr("Archived")}</Pill>
        </PillRail>
        <button type="button" onClick={() => { setQuickParent(null); setQuickOpen(true); }} title="Quick work order — pick a customer, add a title and a few lines" data-testid="btn-quick-wo"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
          <Plus className="w-3.5 h-3.5" />
        </button>
        <div className="ml-auto text-xs text-slate-500 num" data-testid="wo-count">
          {rows.length} {tr(search.trim() ? "matching" : view)}
        </div>
      </div>

      </StickyTop>
      <QuickWOModal open={quickOpen} onOpenChange={(v) => { setQuickOpen(v); if (!v) setQuickParentRaw(null); }} parent={quickParent}
        clientId={quickParent?.client_id || undefined} clients={quickRefs.clients} technicians={quickRefs.technicians} projectManagers={quickRefs.projectManagers}
        onCreated={(wo) => { loadWOs(); navigate(`/work-orders/${wo.id}`); }} />

      <div className="data-table-container">
        <Table data-testid={view === VIEWS.active ? "wo-active-table" : "wo-completed-table"}>
          <TableHeader className="thead-sticky">
            <TableRow>
              <Th {...hdr} field="wo_number" className="w-[120px]">{tr("WO #")}</Th>
              <Th {...hdr} field="title" className="min-w-[220px]">{tr("Name")}</Th>
              <Th {...hdr} field="client_name" className="min-w-[160px]" filter={
                <HeaderFilter label={tr("Customer")} testId="filter-wo-customer" options={customerOptions} value={customerFilter} onChange={setCustomerFilter} />
              }>{tr("Customer")}</Th>
              <Th {...hdr} field="site_address" className="min-w-[200px]">{tr("Address")}</Th>
              <Th {...hdr} field="status" className="w-[110px]" filter={
                <HeaderFilter label={tr("Status")} testId="filter-wo-status" options={statusOptions} value={statusFilter} onChange={setStatusFilter} multi />
              }>{tr("Status")}</Th>
              <Th {...hdr} field="category" className="min-w-[140px]" filter={
                <HeaderFilter label={tr("Category")} testId="wo-category-filter" options={categoryOptions} value={cats} onChange={setCats} multi />
              }>{tr("Category")}</Th>
              <Th {...hdr} field="total" className="w-[110px] num">{tr("Value")}</Th>
              <Th {...hdr} field="scheduled_date" className="w-[100px] num">{tr("Scheduled")}</Th>
              <Th {...hdr} field="completion_date" className="w-[100px] num">{tr("Completion")}</Th>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-sm text-slate-400 py-8" data-testid="wo-empty">
                  {tr("No {view} work orders", { view: tr(view) })}
                </TableCell>
              </TableRow>
            )}
            {grouped
              ? grouped.map((g) => (
                <Fragment key={g.label}>
                  <TableRow className="bg-slate-50 hover:bg-slate-50" data-testid={`wo-group-row-${g.label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <TableCell colSpan={10} className="py-1.5 text-[11px] font-semibold text-slate-600">
                      {g.label} <span className="font-normal text-slate-400 num">{g.rows.length}</span>
                    </TableCell>
                  </TableRow>
                  {g.rows.map((wo) => <WORow key={wo.id} wo={wo} view={view} onOpen={handleOpenWO} onConfirm={setConfirming} onQuickFrom={setQuickParent} />)}
                </Fragment>
              ))
              : rows.map((wo) => <WORow key={wo.id} wo={wo} view={view} onOpen={handleOpenWO} onConfirm={setConfirming} onQuickFrom={setQuickParent} />)}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirming} onOpenChange={(o) => { if (!o && !confirmBusy) setConfirming(null); }}>
        <AlertDialogContent data-testid="wo-confirm-complete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm {confirming?.wo_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.field_completed_by || "The technician"} marked this work order done in the field.
              Confirming acknowledges the close-out and moves the work order to Completed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={BTN.nav} disabled={confirmBusy} data-testid="wo-confirm-complete-cancel">{tr("Not yet")}</AlertDialogCancel>
            <AlertDialogAction className={BTN.write} disabled={confirmBusy} onClick={(e) => { e.preventDefault(); runConfirm(); }} data-testid="wo-confirm-complete-yes">
              {confirmBusy ? "Confirming…" : "Confirm & complete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
